import { GetRecordByIdResult } from '@indigohive/cogfy-types/endpoints/getRecordById'
import { GetCollectionFieldsResultData } from '@indigohive/cogfy-types/endpoints/getCollectionFields'
import type { JsonRecordProperty, UUID } from '@indigohive/cogfy-types'
import { UpdateRecordPropertiesOperationData } from '@indigohive/cogfy-types/endpoints/transactions'
import clsx from 'clsx'
import { useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'

export type JsonRecordPropertyInputProps = {
  collectionId: UUID
  field: Extract<GetCollectionFieldsResultData, { type: 'json' }>
  record: GetRecordByIdResult
  onUpdateRecordProperties?: (data: UpdateRecordPropertiesOperationData) => void
  disabled?: boolean
  stale?: boolean
  error?: boolean
}

export function JsonRecordPropertyInput (props: JsonRecordPropertyInputProps) {
  const { collectionId, field, record, disabled, stale, error } = props

  const property = record.properties[field.id] as JsonRecordProperty | undefined

  const [value, setValue] = useState(
    property?.json?.invalidValue ?? JSON.stringify(property?.json?.value) ?? ''
  )

  const onChange = useDebouncedCallback(
    (value: string) => {
      props.onUpdateRecordProperties?.({
        recordId: record.id,
        collectionId,
        properties: {
          [field.id]: getJsonProperty(value)
        }
      })
    },
    300
  )

  const invalid = property?.json?.invalidValue !== undefined

  return (
    <div>
      <textarea
        disabled={disabled || stale || error}
        className={clsx(
          'textarea textarea-bordered w-full',
          (stale || error) && '!border-none',
          (stale && !error) && 'custom-bg-stale',
          error && 'custom-bg-error',
          invalid && 'textarea-error'
        )}
        value={value}
        onChange={event => {
          setValue(event.target.value)
          onChange(event.target.value)
        }}
      />
    </div>
  )
}

function getJsonProperty (rawValue: string): JsonRecordProperty {
  try {
    return {
      type: 'json',
      json: {
        value: JSON.parse(rawValue)
      }
    }
  } catch {
    return {
      type: 'json',
      json: {
        value: null,
        invalidValue: rawValue
      }
    }
  }
}
